import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthentication } from '@connect-x-app/hybrid-state';
import { Text } from '@connect-x-app/hybrid-ui';
import { Channel, FlightRoomUserStatus, Message, MessageStatus } from '@connect-x-app/shared-domain-models';
import { isPublicMessage } from '@connect-x-app/shared-utils';
import { SideModal } from '@connect-x-app/web-ui';

import { CrewMemberInfoItem } from '../crewMemberInfoItem';

type MessageInfoSideModalProps = {
  channel: Channel;
  message: Message | null;
  onClose: () => void;
  showOfflineText?: boolean;
};

export const MessageInfoSideModal: FC<MessageInfoSideModalProps> = ({ channel, message, onClose, showOfflineText }) => {
  const { t } = useTranslation();
  const { user } = useAuthentication();
  const publicMessage = isPublicMessage(message?.visibility);

  const renderFlightRoomUsers = useCallback(() => {
    const flightRoomUsers =
      channel.users?.filter(
        ({ externalId, status }) => externalId !== user?.externalId && status !== FlightRoomUserStatus.PENDING,
      ) ?? [];

    return flightRoomUsers.map(crewMember => {
      if (message) {
        const receipt = message.receipts?.find(r => crewMember.externalId === r.user);
        const status = receipt?.status ?? MessageStatus.SENT;
        const receiptUpdatedAt = receipt?.updatedAt ?? message.createdAt;
        return (
          <CrewMemberInfoItem
            crewMember={crewMember}
            key={crewMember.externalId}
            messageDeliveryStatus={status}
            receiptUpdatedAt={receiptUpdatedAt}
          />
        );
      }
      return <CrewMemberInfoItem crewMember={crewMember} key={crewMember.externalId} />;
    });
  }, [message, channel.users, user?.externalId]);

  const content = useMemo(() => {
    if (showOfflineText) {
      return <Text>{t('OFFLINE.USERS')}</Text>;
    }
    if (!publicMessage) {
      return <Text>{t('MESSAGE.MESSAGE_INFO.PRIVATE')}</Text>;
    }

    return <>{renderFlightRoomUsers()}</>;
  }, [publicMessage, renderFlightRoomUsers, showOfflineText, t]);

  return (
    <SideModal
      content={content}
      header={{
        rightIcon: { iconName: 'closeCross', onClick: onClose },
        title: { text: t('MESSAGE.MESSAGE_INFO.TITLE') },
      }}
      isVisible={!!message}
      onBackgroundClick={onClose}
    />
  );
};
